import React, { useEffect, useState } from 'react';
import { api, HERITAGE_ASSETS } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { DahliaMark } from '@/components/BrandMark';
import { CalendarClock, Sparkles, Package, Truck } from 'lucide-react';

function Countdown({ target }) {
 const [t, setT] = useState(null);
 useEffect(() => {
 const compute = () => {
 if (!target) return null;
 const diff = new Date(target).getTime() - Date.now();
 if (diff <= 0) return { days: 0, hours: 0, minutes: 0 };
 return {
 days: Math.floor(diff / (1000*60*60*24)),
 hours: Math.floor((diff / (1000*60*60)) % 24),
 minutes: Math.floor((diff / (1000*60)) % 60),
 };
 };
 setT(compute());
 const id = setInterval(() => setT(compute()), 60_000);
 return () => clearInterval(id);
 }, [target]);
 if (!t) return <div className="h-serif text-3xl" style={{ color: 'var(--heritage-plum)' }}>Scheduling…</div>;
 return (
 <div className="flex items-baseline gap-3 h-serif" style={{ color: 'var(--heritage-plum)' }}>
 <div><span className="text-4xl">{t.days}</span><span className="text-sm ml-1">days</span></div>
 <div><span className="text-4xl">{t.hours}</span><span className="text-sm ml-1">hrs</span></div>
 <div><span className="text-4xl">{t.minutes}</span><span className="text-sm ml-1">min</span></div>
 </div>
 );
}

export default function FounderDashboard() {
 const [data, setData] = useState(null);
 const [stages, setStages] = useState([]);

 useEffect(() => {
 (async () => {
 const [me, prod] = await Promise.all([api.get('/founders/me'), api.get('/founders/production')]);
 setData(me.data);
 setStages(prod.data.stages || []);
 })();
 }, []);

 if (!data) return <div style={{ color: 'var(--heritage-plum)' }}>Loading…</div>;
 const m = data.membership || {};
 const done = stages.filter(s => s.completed).length;

 // Next release: 30 days out for demo if backend doesn't have one set
 const nextRelease = data.next_release ? new Date(Date.now() + 30*24*60*60*1000).toISOString() : new Date(Date.now() + 30*24*60*60*1000).toISOString();

 return (
 <div className="space-y-6">
 <Panel className="p-8 relative overflow-hidden">
 <div className="grid md:grid-cols-2 gap-6">
 <div>
 <div className="h-eyebrow">Welcome, Founder</div>
 <div className="h-display text-4xl md:text-5xl mt-1" style={{ color: 'var(--heritage-plum)' }}>{data.user?.name}</div>
 <div className="mt-4 flex flex-wrap gap-4">
 <div><div className="h-eyebrow">Founder Number</div><div className="h-serif text-2xl" style={{ color: 'var(--heritage-plum)' }}>{m.founder_number || ' '}</div></div>
 <div><div className="h-eyebrow">Tier</div><div className="h-serif text-2xl" style={{ color: 'var(--heritage-plum)' }}>{m.tier_name || ' '}</div></div>
 <div><div className="h-eyebrow">Member Since</div><div className="h-serif text-2xl" style={{ color: 'var(--heritage-plum)' }}>{(m.member_since || '').slice(0, 10) || ' '}</div></div>
 </div>
 </div>
 <div>
 <div className="h-eyebrow flex items-center gap-2"><CalendarClock size={14} /> Next release</div>
 <div className="mt-2"><Countdown target={nextRelease} /></div>
 <div className="text-sm opacity-80 mt-1" style={{ color: 'var(--heritage-plum)' }}>Your Tier {m.tier_name?.includes('Two') ? 'Two' : 'One'} bundle enters production immediately after next release.</div>
 </div>
 </div>
 </Panel>

 <div className="grid md:grid-cols-3 gap-6">
 <Panel className="p-6">
 <div className="h-eyebrow flex items-center gap-2"><Truck size={14} /> Order status</div>
 <div className="h-serif text-3xl mt-1" style={{ color: 'var(--heritage-plum)' }}>{done}/{stages.length} stages</div>
 <div className="h-2 rounded-full mt-3" style={{ background: 'rgba(75,24,56,0.15)' }}>
 <div className="h-2 rounded-full" style={{ width: `${(done/Math.max(1, stages.length))*100}%`, background: 'var(--heritage-plum)' }} />
 </div>
 <div className="text-sm opacity-80 mt-2" style={{ color: 'var(--heritage-plum)' }}>{stages.find(s => !s.completed)?.stage_label || 'Delivered'}</div>
 </Panel>
 <Panel className="p-6">
 <div className="h-eyebrow flex items-center gap-2"><Sparkles size={14} /> Exclusive Colourway</div>
 <div className="h-serif text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>{m.design_selections?.founder_hoodie_color || 'Not chosen yet'}</div>
 <div className="flex items-center gap-2 mt-3">
 <div className="w-8 h-8 rounded-full" style={{ background: '#4B1838' }} />
 <div className="w-8 h-8 rounded-full" style={{ background: '#9A4B7E' }} />
 <div className="w-8 h-8 rounded-full" style={{ background: '#8FA34B' }} />
 <div className="w-8 h-8 rounded-full" style={{ background: '#D97CA0' }} />
 </div>
 </Panel>
 <Panel className="p-6">
 <div className="h-eyebrow flex items-center gap-2"><Package size={14} /> Bundle</div>
 <div className="h-serif text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>{m.tier_name || 'Bundle'}</div>
 <div className="text-sm opacity-80 mt-2" style={{ color: 'var(--heritage-plum)' }}>Tracksuit(s), socks, hat, mystery box and secret release item included.</div>
 </Panel>
 </div>

 <Panel className="p-6">
 <div className="h-eyebrow mb-3">From the atelier</div>
 <div className="grid md:grid-cols-3 gap-4">
 {[HERITAGE_ASSETS.tracksuitField, HERITAGE_ASSETS.editorial, HERITAGE_ASSETS.swatches].map((src) => (
 <img key={src} src={src} alt="" className="w-full h-48 object-cover rounded-lg" />
 ))}
 </div>
 </Panel>
 </div>
 );
}
